import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Info } from "lucide-react";
import PaperWorkspace from "../paper/PaperWorkspace";
import { API, Card, FeedBody, Skeleton, useFeed, useMarkets } from "./ui";
import {
  ACTIVE_SCOPE, ago, base, count, dayKey, fmtTime, money, pct, price, riskAtStop, toDate, tone, tradePnl,
} from "./format";

// ── WEEX validation, one dot per row ─────────────────────────────────────────
const WEEX = {
  DEMO_VALIDATED:   { c: "#4ade80", t: "WEEX demo order matched" },
  MARKET_VALIDATED: { c: "#4ad6ff", t: "WEEX depth check passed" },
  DEMO_PENDING:     { c: "#f5b042", t: "WEEX demo pending" },
  DEMO_DRIFT:       { c: "#ff8a94", t: "WEEX demo drift" },
  DEMO_REJECTED:    { c: "#ff5d6c", t: "WEEX demo rejected" },
  DRIFT:            { c: "#f5b042", t: "WEEX price drift" },
  UNSUPPORTED:      { c: "#8b9488", t: "Not listed on WEEX" },
  WEEX_UNAVAILABLE: { c: "#ff8a94", t: "WEEX offline" },
};
export function weexOf(p) {
  const v = p?.weex_validation || {};
  const status = String(v.status || v.market_status || "SYNCING").toUpperCase();
  const meta = WEEX[status] || { c: "#8b9488", t: "WEEX check syncing" };
  const reason = v.demo_error || v.market_reason || "";
  return { status, color: meta.c, title: reason ? `${meta.t} — ${reason}` : meta.t,
    passed: status === "DEMO_VALIDATED" || status === "MARKET_VALIDATED", demoPassed: status === "DEMO_VALIDATED" };
}

export const posKey = (p) => `${p.account_id}:${p.id || p.symbol}`;

// ── Rail: positions ──────────────────────────────────────────────────────────
function PositionsList({ positions, loading, error, selectedKey, onSelect, onDetail, weexShadow }) {
  const rows = useMemo(() => [...positions].sort((a, b) => Number(a.unrealized_pnl || 0) - Number(b.unrealized_pnl || 0)), [positions]);
  const demo = positions.filter((p) => weexOf(p).demoPassed).length;
  const depth = positions.filter((p) => weexOf(p).passed).length;
  const weexLine = !weexShadow?.market_feed_online
    ? "WEEX validation feed offline"
    : `WEEX demo check ${demo}/${positions.length} passed · depth ${depth}/${positions.length}`;

  return (
    <div className="mk-rail-pane">
      <p className="mk-rail-note">{loading && !positions.length ? "Syncing positions…" : weexLine}</p>
      {error && !positions.length ? <p className="mk-rail-empty is-error">{error}</p>
        : loading && !positions.length ? <Skeleton rows={6} />
          : !rows.length ? <p className="mk-rail-empty">No open positions. AEON is waiting for an eligible setup.</p> : (
            <ul className="mk-pos-list">
              {rows.map((p) => {
                const k = posKey(p);
                const w = weexOf(p);
                const pnl = Number(p.unrealized_pnl || 0);
                const side = String(p.direction || "").toUpperCase();
                return (
                  <li key={k} className={`mk-pos ${selectedKey === k ? "is-on" : ""}`}>
                    <button type="button" className="mk-pos-main" onClick={() => onSelect(p)}
                      aria-pressed={selectedKey === k} title={`Show ${base(p.symbol)} with this position's levels`}>
                      <i className={`mk-side-bar ${side === "LONG" ? "is-long" : "is-short"}`} aria-hidden />
                      <span className="mk-pos-sym">{base(p.symbol)}</span>
                      <span className={`mk-pos-side ${side === "LONG" ? "is-up" : "is-down"}`}>{side === "LONG" ? "L" : "S"} {Number(p.leverage || 0)}x</span>
                      <span className="mk-pos-acct">{p.account_id}</span>
                      <span className={`mk-num mk-pos-pct ${tone(pnl)}`}>{pct(p.unrealized_pnl_pct ?? p.pnl_pct, { sign: true, dec: 1 })}</span>
                      <span className={`mk-num mk-pos-usd ${tone(pnl)}`}>{money(pnl)}</span>
                      <i className="mk-dot" style={{ background: w.color }} title={w.title} aria-label={w.title} role="img" />
                    </button>
                    <button type="button" className="mk-icon-btn" onClick={() => onDetail(p)} aria-label={`Details and close for ${base(p.symbol)} ${p.account_id}`} title="Details / close">
                      <Info aria-hidden />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
    </div>
  );
}

// ── Rail: activity (ALERT + OPEN of the same trade merged) ───────────────────
function mergeEvents(events) {
  const out = [];
  const used = new Set();
  events.forEach((e, i) => {
    if (used.has(i)) return;
    if (e.type === "alert" || e.type === "open") {
      const partner = events.findIndex((o, j) => j !== i && !used.has(j)
        && o.type === (e.type === "alert" ? "open" : "alert")
        && o.symbol === e.symbol && o.direction === e.direction
        && Math.abs((toDate(o.timestamp)?.getTime() || 0) - (toDate(e.timestamp)?.getTime() || 0)) < 180000);
      if (partner >= 0) {
        used.add(partner);
        const open = e.type === "open" ? e : events[partner];
        const alert = e.type === "alert" ? e : events[partner];
        out.push({ ...open, type: "alert+open", confidence: open.confidence ?? alert.confidence,
          reason: [open.account, open.engine].filter(Boolean).join(" · ") });
        used.add(i);
        return;
      }
    }
    out.push(e.type === "open" ? { ...e, reason: [e.account, e.engine].filter(Boolean).join(" · ") } : e);
    used.add(i);
  });
  return out;
}

const EVENT_LABEL = { "alert+open": "Alert → open", alert: "Alert", open: "Open", close: "Close" };

function ActivityList() {
  const { tz } = useMarkets();
  const feed = useFeed(`${API}/system/activity-feed?limit=40`, { interval: 15000 });
  return (
    <div className="mk-rail-pane">
      <FeedBody feed={feed} rows={6}>
        {(d) => {
          const rows = mergeEvents(d.events || []);
          if (!rows.length) return <p className="mk-rail-empty">No recent activity.</p>;
          return (
            <table className="mk-table mk-activity">
              <thead><tr><th>Time</th><th>Event</th><th>Side</th><th>Symbol</th><th className="r">Conf / P&amp;L</th><th>Reason</th></tr></thead>
              <tbody>
                {rows.map((e, i) => (
                  <tr key={i}>
                    <td className="mk-num" title={fmtTime(e.timestamp, tz, { date: true })}>{fmtTime(e.timestamp, tz).replace(/ [A-Z]{2,4}$/, "")}</td>
                    <td>{EVENT_LABEL[e.type] || e.type}</td>
                    <td className={e.direction === "LONG" ? "is-up" : e.direction === "SHORT" ? "is-down" : ""}>{e.direction === "LONG" ? "L" : e.direction === "SHORT" ? "S" : "—"}</td>
                    <td>{base(e.symbol)}</td>
                    <td className={`r mk-num ${e.pnl != null ? tone(e.pnl) : ""}`}>
                      {e.pnl != null ? money(e.pnl) : e.confidence != null ? `${Math.round(e.confidence)}%` : "—"}
                    </td>
                    <td className="mk-trunc" title={String(e.reason || "").replace(/_/g, " ")}>{String(e.reason || (e.outcome ? String(e.outcome).toLowerCase() : "")).replace(/_/g, " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }}
      </FeedBody>
      <p className="mk-rail-note">Times in {fmtTime(new Date(), tz).split(" ").pop()}</p>
    </div>
  );
}

function Rail({ tab, setTab, watchlist, positionsPane }) {
  const tabs = [["positions", "Positions"], ["watchlist", "Watchlist"], ["activity", "Activity"]];
  return (
    <div className="mk-rail">
      <div className="mk-rail-tabs" role="tablist" aria-label="Live desk rail">
        {tabs.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "is-on" : ""}
            onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      <div className="mk-rail-body">
        <div hidden={tab !== "positions"} className="mk-rail-slot">{positionsPane}</div>
        {/* Kept mounted so its OKX subscription keeps feeding the ticket and dock. */}
        <div hidden={tab !== "watchlist"} className="mk-rail-slot is-watchlist">{watchlist}</div>
        {tab === "activity" ? <div className="mk-rail-slot"><ActivityList /></div> : null}
      </div>
    </div>
  );
}

// ── Lower cards ──────────────────────────────────────────────────────────────
function TodaySession({ trades }) {
  const { tz } = useMarkets();
  const today = dayKey(new Date(), tz);
  const list = trades.filter((t) => dayKey(t.closed_at || t.exit_time, tz) === today);
  const wins = list.filter((t) => tradePnl(t) > 0).length;
  const losses = list.filter((t) => tradePnl(t) < 0).length;
  const wr = wins + losses ? (wins / (wins + losses)) * 100 : null;
  const sorted = [...list].sort((a, b) => tradePnl(b) - tradePnl(a));
  const best = sorted[0];
  const worst = sorted[sorted.length - 1];
  return (
    <Card title="Today's session" scope={`Closed since midnight ${fmtTime(new Date(), tz).split(" ").pop()} · ${ACTIVE_SCOPE} · P&L total is in the header`}>
      <div className="mk-stat-row">
        <div><span className="mk-label">Closed trades</span><strong className="mk-num mk-big">{count(list.length)}</strong></div>
        <div><span className="mk-label">Wins / losses</span><strong className="mk-num mk-big"><span className="is-up">{wins}</span> / <span className="is-down">{losses}</span></strong></div>
      </div>
      <div className="mk-meter" aria-label="Win rate today">
        <div className="mk-meter-head"><span className="mk-label">Win rate today</span><b className="mk-num">{wr == null ? "—" : pct(wr, { dec: 0 })}</b></div>
        <div className="mk-meter-track"><i style={{ width: `${wr ?? 0}%` }} /></div>
      </div>
      {list.length ? (
        <dl className="mk-kv">
          <div><dt>Best</dt><dd className={`mk-num ${tone(tradePnl(best))}`}>{money(tradePnl(best))} <span>{base(best.symbol)}</span></dd></div>
          <div><dt>Worst</dt><dd className={`mk-num ${tone(tradePnl(worst))}`}>{money(tradePnl(worst))} <span>{base(worst.symbol)}</span></dd></div>
        </dl>
      ) : <p className="mk-empty">No trades have closed today yet.</p>}
    </Card>
  );
}

function DirectionalEdge({ trades }) {
  const s = useMemo(() => {
    const side = (d) => {
      const list = trades.filter((t) => String(t.direction || "").toUpperCase() === d);
      const wins = list.filter((t) => tradePnl(t) > 0).length;
      const graded = list.filter((t) => tradePnl(t) !== 0).length;
      return { n: list.length, wr: graded ? (wins / graded) * 100 : null, pnl: list.reduce((a, t) => a + tradePnl(t), 0) };
    };
    return { short: side("SHORT"), long: side("LONG") };
  }, [trades]);
  const total = s.short.n + s.long.n;
  const shortShare = total ? (s.short.n / total) * 100 : 0;
  return (
    <Card title="Directional edge" scope={`All closed trades · ${ACTIVE_SCOPE} · realized P&L`}>
      <div className="mk-split">
        {[["Short book", s.short, "is-short"], ["Long book", s.long, "is-long"]].map(([label, x, cls]) => (
          <div key={label} className={cls}>
            <span className="mk-label">{label}</span>
            <strong className={`mk-num mk-big ${tone(x.pnl)}`}>{money(x.pnl)}</strong>
            <span className="mk-sub">{x.wr == null ? "—" : pct(x.wr, { dec: 0 })} win rate · {count(x.n)} trades</span>
          </div>
        ))}
      </div>
      <div className="mk-meter">
        <div className="mk-meter-head"><span className="mk-label">Short share of closed trades</span><b className="mk-num">{pct(shortShare, { dec: 0 })}</b></div>
        <div className="mk-meter-track is-dual"><i style={{ width: `${shortShare}%` }} /></div>
      </div>
    </Card>
  );
}

export function PortfolioRisk({ title = "Portfolio risk" }) {
  const feed = useFeed(`${API}/portfolio/correlation`, { interval: 20000 });
  return (
    <Card title={title} scope="Open positions · per-account heat = margin in use ÷ account balance">
      <FeedBody feed={feed} rows={4}>
        {(d) => (
          <>
            {d.correlation_warning ? (
              <p className="mk-warn"><AlertTriangle aria-hidden /> {d.correlation_warning}</p>
            ) : null}
            <ul className="mk-heat-list">
              {(d.accounts || []).map((a) => (
                <li key={a.account_id}>
                  <span className="mk-heat-name">{a.account_id}</span>
                  <span className="mk-meter-track"><i style={{ width: `${Math.min(100, a.heat_pct)}%` }} className={a.heat_pct > 70 ? "is-hot" : a.heat_pct > 40 ? "is-warm" : ""} /></span>
                  <span className="mk-num">{pct(a.heat_pct)}</span>
                </li>
              ))}
            </ul>
            <dl className="mk-kv">
              <div><dt>Direction skew</dt><dd className="mk-num">{d.short_pct}% short · {d.long_pct}% long</dd></div>
              <div><dt>Distinct symbols</dt><dd className="mk-num">{d.unique_symbols}</dd></div>
            </dl>
          </>
        )}
      </FeedBody>
    </Card>
  );
}

// ── Live Desk ────────────────────────────────────────────────────────────────
export default function LiveDesk({
  positions, positionsLoading, positionError, weexShadow, activeTrades, onOpenPosition,
}) {
  const [railTab, setRailTab] = useState("positions");
  const [selectedKey, setSelectedKey] = useState(null);
  const [symbol, setSymbol] = useState(null);
  const [stageH, setStageH] = useState(640);
  const [overhead, setOverhead] = useState(120);   // chart header etc. around the canvas
  const stageRef = useRef(null);
  const userPicked = useRef(false);

  // On load, show the symbol with the largest open risk (loss at stop).
  useEffect(() => {
    if (userPicked.current || !positions.length) return;
    const top = [...positions].sort((a, b) => riskAtStop(b) - riskAtStop(a))[0];
    if (top) { setSymbol(base(top.symbol)); setSelectedKey(posKey(top)); }
  }, [positions]);

  // Chart fills the viewport under the header (min 520px).
  useLayoutEffect(() => {
    const fit = () => {
      const el = stageRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const h = Math.max(520, Math.round(window.innerHeight - top - 12));
      setStageH((prev) => (Math.abs(prev - h) > 8 ? h : prev));
    };
    fit();
    const id = setTimeout(fit, 400);
    window.addEventListener("resize", fit);
    return () => { clearTimeout(id); window.removeEventListener("resize", fit); };
  }, []);

  const selectedPos = positions.find((p) => posKey(p) === selectedKey) || null;
  const activeSymbol = symbol || "BTC";
  const chartPositions = useMemo(() => {
    const onSym = positions.filter((p) => base(p.symbol) === activeSymbol);
    const list = selectedPos && base(selectedPos.symbol) === activeSymbol ? [selectedPos] : onSym;
    return list.map((p) => ({
      symbol: p.symbol, entry_price: p.entry_price, stop_price: p.stop_loss ?? p.stop_price,
      target_price: p.take_profit ?? p.target_price, liquidation_price: p.liquidation_price, direction: p.direction,
    }));
  }, [positions, selectedPos, activeSymbol]);
  const markSymbols = useMemo(() => [...new Set(positions.map((p) => base(p.symbol)))], [positions]);

  const pickSymbol = (s) => {
    userPicked.current = true;
    setSymbol(s);
    if (!selectedPos || base(selectedPos.symbol) !== s) setSelectedKey(null);
  };
  const pickPosition = (p) => {
    userPicked.current = true;
    setSelectedKey(posKey(p));
    setSymbol(base(p.symbol));
  };

  const positionsPane = (
    <PositionsList positions={positions} loading={positionsLoading} error={positionError} weexShadow={weexShadow}
      selectedKey={selectedKey} onSelect={pickPosition} onDetail={onOpenPosition} />
  );

  // Canvas height = stage minus whatever the chart wraps around it (its header,
  // borders) — measured, so the chart's bottom lands exactly on the viewport.
  const chartHeight = Math.max(380, stageH - overhead);
  useLayoutEffect(() => {
    const el = stageRef.current?.querySelector('[data-testid="trading-chart"]');
    if (!el) return;
    const extra = Math.round(el.getBoundingClientRect().height - chartHeight);
    if (extra > 0 && Math.abs(extra - overhead) > 2) setOverhead(extra);
  }, [chartHeight, overhead]);

  return (
    <div className="mk-live">
      <div className="mk-stage" ref={stageRef} style={{ "--stage-h": `${stageH}px` }}>
        <PaperWorkspace
          symbol={activeSymbol}
          onSymbolChange={pickSymbol}
          chartPositions={chartPositions}
          chartHeight={chartHeight}
          liveHeader
          markSymbols={markSymbols}
          rail={({ watchlist }) => (
            <Rail tab={railTab} setTab={setRailTab} watchlist={watchlist} positionsPane={positionsPane} />
          )}
        />
      </div>
      {selectedPos ? (
        <p className="mk-chart-caption">
          Showing <b>{base(selectedPos.symbol)} {selectedPos.direction} {Number(selectedPos.leverage)}x · {selectedPos.account_id}</b>:
          {" "}entry {price(selectedPos.entry_price)} · stop {price(selectedPos.stop_loss ?? selectedPos.stop_price)} · target {price(selectedPos.take_profit ?? selectedPos.target_price)} · liquidation {price(selectedPos.liquidation_price)}
          {" "}· opened {ago(selectedPos.opened_at)} ago
        </p>
      ) : null}
      <div className="mk-grid-3">
        <TodaySession trades={activeTrades} />
        <DirectionalEdge trades={activeTrades} />
        <PortfolioRisk />
      </div>
    </div>
  );
}
